/**
 * `crew` — the touring party. Hot path is 4 fields (name/role/phone/email —
 * the v1 60-field wall is dead); the profile lives behind "Add details" with
 * STRICT PII tags: identity documents and payment details are `crew_self`
 * (the person themselves + owner/editor — NEVER other crew, NEVER promoters).
 * The chat-inference substrate lives in the descriptions (e.g. "kosher" →
 * religious_observance — the exact friction Eduardo reported in v1).
 */
import { z } from "zod";
import { defineCollection, field } from "../define.ts";

const DAY = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD");

export const crew = defineCollection({
  key: "crew",
  labelKey: "collections.crew",
  icon: "users",
  sheetTab: "Crew",
  dedupFields: ["name", "role"],
  sort: [{ field: "name" }],
  split: "people",
  chatHint:
    "The touring party: artist, TM, VJ, sound engineer, driver… Dietary/religious/medical facts " +
    "belong on the matching profile field (e.g. 'Ben is kosher' → religious_observance = kosher), " +
    "never as free-text notes. Identity documents and bank details are crew_self scoped.",
  fields: {
    name: field({
      type: z.string().min(1),
      tier: "hot",
      pii: "crew",
      labelKey: "crew.name",
      sheet: { column: "Name" },
      description: "Full name. Companies abbreviate names differently and typos happen — when a new name is close to an existing member, ask before creating a duplicate.",
    }),
    role: field({
      type: z.string().optional(),
      tier: "hot",
      pii: "crew",
      labelKey: "crew.role",
      sheet: { column: "Role" },
      suggestedChips: ["Artist", "Tour manager", "VJ", "Sound engineer", "Driver", "Photographer"],
      description: "Role on the road: artist, tour manager, VJ, FOH/sound engineer, driver…",
    }),
    phone: field({
      type: z.string().optional(),
      tier: "hot",
      pii: "crew",
      labelKey: "crew.phone",
      input: "tel",
      sheet: { column: "Phone" },
      description: "Mobile, international format preferred.",
    }),
    email: field({
      type: z.string().optional(),
      tier: "hot",
      pii: "crew",
      labelKey: "crew.email",
      input: "email",
      sheet: { column: "Email" },
      description: "Email address.",
    }),
    stage_name: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "crew.stage_name",
      sheet: { column: "Stage name" },
      description: "Artist/stage name when different from the legal name (opsec: public surfaces use THIS, never the legal name).",
    }),
    home_base: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "crew.home_base",
      sheet: { column: "Home base" },
      description: "Home city — where their travel usually starts and ends (e.g. Thibault: Toulouse).",
    }),
    dietary: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "crew.dietary",
      suggestedChips: ["vegan", "vegetarian", "gluten-free", "no pork", "allergies"],
      sheet: { column: "Dietary" },
      description: "Dietary requirements: vegan, vegetarian, allergies, gluten-free… ('X is vegan' lands here.)",
    }),
    religious_observance: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "crew.religious_observance",
      suggestedChips: ["kosher", "halal", "shabbat"],
      sheet: { column: "Religious" },
      description:
        "Religious observances affecting food or scheduling: kosher, halal, shabbat, prayer times. " +
        "'Ben is kosher' or 'Omri keeps shabbat' land HERE, not in dietary and not in notes.",
    }),
    medical: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew_self",
      labelKey: "crew.medical",
      sheet: { column: "Medical" },
      description: "Medical notes (allergies severity, medication, emergency info). Visible only to the person, owner and editor.",
    }),
    date_of_birth: field({
      type: DAY.optional(),
      tier: "detail",
      pii: "crew_self",
      labelKey: "crew.date_of_birth",
      sheet: { column: "DOB" },
      description: "Date of birth (bookings, manifests).",
    }),
    nationality: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew_self",
      labelKey: "crew.nationality",
      sheet: { column: "Nationality" },
      description: "Nationality on the passport used for touring.",
    }),
    passport_number: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew_self",
      labelKey: "crew.passport_number",
      sheet: { column: "Passport" },
      description: "Passport number. crew_self scoped: the person, owner and editor only.",
    }),
    passport_expiry: field({
      type: DAY.optional(),
      tier: "detail",
      pii: "crew_self",
      labelKey: "crew.passport_expiry",
      sheet: { column: "Passport exp" },
      description: "Passport expiry date — the suggestions system warns when a tour outlives it.",
    }),
    visa_notes: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew_self",
      labelKey: "crew.visa_notes",
      sheet: { column: "Visas" },
      description: "Visa statuses and numbers by country (US ESTA/P1, AU, etc.).",
    }),
    frequent_flyer: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew_self",
      labelKey: "crew.frequent_flyer",
      sheet: { column: "FF" },
      description: "Frequent-flyer / KTN / Global Entry numbers.",
    }),
    iban: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "financial",
      labelKey: "crew.iban",
      sheet: { column: "IBAN" },
      description: "IBAN for per-diems/fees. Financial scope: owner and editor only.",
    }),
    bic: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "financial",
      labelKey: "crew.bic",
      sheet: { column: "BIC" },
      description: "BIC/SWIFT. Financial scope.",
    }),
    emergency_contact: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew_self",
      labelKey: "crew.emergency_contact",
      sheet: { column: "Emergency" },
      description: "Emergency contact: name + phone + relationship.",
    }),
    tech_rig: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "crew.tech_rig",
      sheet: { column: "Tech rig" },
      description:
        "Role-specific technical needs as free text (v1's 10 per-role rig sections collapse here: " +
        "booth config for artists, console/monitors for engineers, cue system for stage managers…).",
    }),
    notes: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "crew.notes",
      sheet: { column: "Notes" },
      description: "Anything else about this member.",
    }),
  },
});
